// Canonical engine wire emit for a WhatsApp chat. Engines (chrome
// extension + baileys pod) project their raw store shape — WAWeb's
// Chat model for chrome, baileys-sdk's chat metadata for baileys — to
// this slim shape at the IPC boundary. Backend consumers (v1
// /v1/chats|groups|newsletters mappers, contracts) read from here.
//
// Single canonical field per concept (#63 / #64):
//   chatId          — bare JID string. The redundant `_serialized`
//                     alias was removed — engines emit `chatId` only.
//   name            — display name. Engine resolves WAWeb's
//                     `chat.formattedTitle ?? chat.name` at emit time;
//                     the `formattedTitle` alias was dropped from
//                     the wire (#63).
//   t               — unix-seconds last-message timestamp. Single
//                     source — the `lastMessageTimestamp` /
//                     `lastMessageAt` aliases were dropped (#64).
//   chatType        — 'contact' | 'group' | 'newsletter'. Replaces the
//                     former isGroup/isNewsletter boolean pair: the
//                     three kinds are mutually exclusive, so one enum
//                     is the single source of truth.
//
// Rich, kind-specific metadata rides in optional sub-blocks so the
// unified query tool (waQueryContactsAndChatsTool) can serve
// /v1/groups + /v1/newsletters losslessly:
//   group       — present on chatType==='group' rows.
//   newsletter  — present on chatType==='newsletter' rows.
//
// The RAW WAWeb Chat shape (with wid id, many internal flags:
// chatlistPreview, lastReceivedKey, draftMessage, disappearingMode*,
// tcToken*, limitSharing, etc.) lives inside the chrome extension per #74.

import { z } from 'zod';
import { WireJidSchema } from '../../contract/wire-identity';
import { BtSlimMessageSchema } from './message';
import { BtSlimParticipantSchema } from './participant';
import { coerceLegacyChatId, coerceLegacyChatType } from './legacy-id';

/** A chat's kind. Replaces the old isGroup/isNewsletter boolean pair. */
export const ChatTypeEnum = z.enum(['contact', 'group', 'newsletter']);
export type ChatType = z.infer<typeof ChatTypeEnum>;

/** Group-only metadata, present only on chatType==='group' rows.
 *  Populated from WAWeb's chat.groupMetadata at engine emit time. Every
 *  field is optional/nullable because a group whose metadata hasn't been
 *  synced yet exposes only a subset. */
export const BtSlimChatGroupInfoSchema = z.object({
  participantCount: z.number().int().nullable().optional(),
  description: z.string().nullable().optional(),
  owner: WireJidSchema.nullable().optional(),
  /** Unix-seconds group-creation timestamp. */
  createdAt: z.number().nullable().optional(),
  /** Admins-only edit info. */
  restrict: z.boolean().nullable().optional(),
  /** Admins-only post. */
  announce: z.boolean().nullable().optional(),
  /** The connected account cannot post here (e.g. an announcement group where
   *  it is not an admin, or a group it left). Read by the Team Inbox composer. */
  isReadOnly: z.boolean().optional(),
  /** The group's members (with `isAdmin`), read by the Team Inbox header and
   *  its composer's admin check. */
  participants: z.array(BtSlimParticipantSchema).optional(),
});
export type BtSlimChatGroupInfo = z.infer<typeof BtSlimChatGroupInfoSchema>;

/** Newsletter-only metadata, present only on chatType==='newsletter' rows. */
export const BtSlimChatNewsletterInfoSchema = z.object({
  description: z.string().nullable().optional(),
  subscribers: z.number().int().nullable().optional(),
  invite: z.string().nullable().optional(),
  verification: z.enum(['VERIFIED', 'UNVERIFIED']).nullable().optional(),
  /** Unix-seconds creation timestamp. */
  createdAt: z.number().nullable().optional(),
});
export type BtSlimChatNewsletterInfo = z.infer<typeof BtSlimChatNewsletterInfoSchema>;

const BtSlimChatObjectSchema = z.object({
  /** Bare JID string — the single canonical chat identity. */
  chatId: WireJidSchema,
  /** Display name. Resolution order (list, get-chat, group and participants
   *  alike): saved contact name → pushname / verified name → group subject /
   *  newsletter name → `+<digits>` for a resolved person. `null` only for an
   *  unresolved `@lid` with no name source. */
  name: z.string().nullable(),
  /** contact | group | newsletter — replaces isGroup/isNewsletter. */
  chatType: ChatTypeEnum,
  /** Unix-seconds last-message timestamp; null when the chat has no
   *  messages yet (cold chat, just opened). */
  t: z.number().nullable(),
  unreadCount: z.number().int().nullable().optional(),
  /** Pinned in the chat list. */
  pinned: z.boolean().optional(),
  /** Archived. Default list queries EXCLUDE these unless
   *  includeArchive=true is passed to the query tool. */
  archived: z.boolean().optional(),
  /**
   * Last-message preview, emitted by the engine ONLY when the query was
   * made with `includeLastMessage: true`. The engine already does the
   * heavy lifting — media messages collapse to their caption or a
   * `[type]` placeholder so raw bytes never leak (see chat-models
   * createLeanChat). Declared here so the fields survive the
   * engine-dispatch boundary parse (zod strips undeclared keys);
   * absent on rows queried without the flag.
   */
  /** The chat's last message, emitted when the query sets `includeLastMessage`. */
  lastMessage: BtSlimMessageSchema.optional(),
  /** Muted in the chat list. */
  muted: z.boolean().optional(),
  /** Derived from `lastMessage`. @5.0b delete. */
  lastMessageText: z.string().optional(),
  /** Derived from `lastMessage`. @5.0b delete. */
  lastMessageFromMe: z.boolean().optional(),
  /** Group-specific metadata; present only on chatType==='group' rows. */
  group: BtSlimChatGroupInfoSchema.optional(),
  /** Newsletter-specific metadata; present only on chatType==='newsletter'. */
  newsletter: BtSlimChatNewsletterInfoSchema.optional(),
});

/** Canonical chat wire schema. Wrapped in legacy coercions so a version-skewed
 *  (pre-rename) gateway parses cleanly: coerceLegacyChatId backfills the
 *  `id`/`_serialized`→`chatId` rename, then coerceLegacyChatType backfills the
 *  `isGroup`/`isNewsletter`→`chatType` change (order matters — the latter reads
 *  the `chatId` the former populated). Both are no-ops once the emitter is
 *  current. */
export const BtSlimChatSchema = z.preprocess(
  (raw) => coerceLegacyChatType(coerceLegacyChatId(raw)),
  BtSlimChatObjectSchema,
);
export type BtSlimChat = z.infer<typeof BtSlimChatSchema>;
